'use client'
import classNames from 'classnames'
import Link from 'next/link'
import type { FC, MouseEvent } from 'react'
import { useEffect, useState } from 'react'
import { AddressPickerDesktop } from '../address-picker-desktop/AddressPickerDesktop'
import type { HeaderColorType } from '../Header'
import { UserButton } from '../user-button/UserButton'
import styles from './HeaderDesktop.module.scss'
import { useDeliveryAddress } from '@/delivery-address/client'
import { useLocalisation } from '@/tools/i18n/client'
import { useErrorHandling } from '@/tools/o11y/client'
import {
  ArrowForwardIcon,
  LocationMarkerIcon,
  LogoSize,
  LogoSurface,
  LogoVariant,
  MainLogo,
  TextInput,
} from '@/tools/ui'
import { LocationBanner } from '@/tools/ui/components/header/ui/location-banner/LocationBanner'
import { LocationScrollDrawer } from '@/tools/ui/components/header/ui/location-scroll-drawer/LocationScrollDrawer'
import { UserSearchInput } from '@/tools/ui/components/header/ui/user-search-input/UserSearchInput'
import { AddressBookModal } from '@/user/address-book/ui/AddressBookModal'

interface Props {
  color?: HeaderColorType
  className?: string
  hasScrollLocationDrawer?: boolean
  shouldShowAddress?: boolean
  hasLocationBanner?: boolean
  shouldShowSearch?: boolean
  searchType: string
  isIntersecting: boolean
}

export const HeaderDesktop: FC<Props> = ({
  className,
  shouldShowAddress,
  shouldShowSearch,
  hasLocationBanner,
  hasScrollLocationDrawer,
  color,
  searchType,
  isIntersecting,
}) => {
  const { getLanguage, t } = useLocalisation()
  const language = getLanguage()
  const { deliveryAddress } = useDeliveryAddress()
  const { withErrorTracking, execWithErrorTracking } = useErrorHandling()
  const [isSearchVisible, setIsSearchVisible] = useState(false)
  const [isOpenAddressBook, setIsOpenAddressBook] = useState(false)

  const showLocationBanner = hasLocationBanner && !deliveryAddress
  const isBrandHeader = color === 'brand'
  const showLocationScrollDrawer = Boolean(
    hasScrollLocationDrawer && !deliveryAddress && isIntersecting
  )

  const handleOpenAddressBook = withErrorTracking(() => {
    setIsOpenAddressBook(true)
  })

  const handleCloseAddressBook = withErrorTracking(() => {
    setIsOpenAddressBook(false)
  })

  const handleSearchClick = withErrorTracking((event: MouseEvent<HTMLInputElement>) => {
    event.stopPropagation()
  })

  //Temporary hardcoded logic to test the scroll effect
  //TODO: Remove this and implement the real logic when there is a page with searchInput
  useEffect(() => {
    execWithErrorTracking(async () => {
      if (typeof window !== 'undefined') {
        const handleScroll = withErrorTracking(() => {
          window.scrollY > 200 ? setIsSearchVisible(true) : setIsSearchVisible(false)
        })
        window.addEventListener('scroll', handleScroll)

        return () => {
          window.removeEventListener('scroll', handleScroll)
        }
      }
    })
  }, [execWithErrorTracking, withErrorTracking])
  //Temporary hardcoded logic to test the scroll effect

  return (
    <div className={classNames(styles.headerDesktop, className)} id={'header-desktop'}>
      <section className={styles.topContainer}>
        <div className={styles.leftSection}>
          <Link className={styles.logoLink} href={`/${language}`}>
            <MainLogo
              className={styles.logoImage}
              size={LogoSize.medium}
              surface={LogoSurface.onBrand}
              variant={LogoVariant.logotype}
            />
          </Link>
        </div>
        <div className={styles.centerSection} role="button" onClick={handleOpenAddressBook}>
          {shouldShowAddress ? (
            deliveryAddress?.text ? (
              <>
                {isSearchVisible && shouldShowSearch ? (
                  <div onClick={handleSearchClick}>
                    <UserSearchInput searchType={searchType} />
                  </div>
                ) : (
                  <AddressPickerDesktop addressText={deliveryAddress.text} />
                )}
              </>
            ) : (
              !isBrandHeader && (
                <TextInput
                  readonly
                  aria-label={t('address-list.search-address')}
                  leadingIcon={LocationMarkerIcon}
                  placeholder={t('landing.address-picker')}
                  trailingIcon={ArrowForwardIcon}
                />
              )
            )
          ) : null}
        </div>
        <div className={styles.rightSection}>
          <UserButton color={color} isIntersecting={isIntersecting} />
        </div>
      </section>
      <AddressBookModal
        isOpen={isOpenAddressBook}
        onClose={handleCloseAddressBook}
        onSelect={handleCloseAddressBook}
      />
      {showLocationBanner && (
        <LocationBanner
          // TODO: Put real data here based on routing
          cityName={'Barcelona'}
          className={styles.bottomContainer}
          isStorePage={false}
        />
      )}
      <LocationScrollDrawer isShown={showLocationScrollDrawer} />
    </div>
  )
}
